import { useState, useMemo, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, comics, Comic } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'popularity' | 'rating' | 'latest'>('popularity');

  useEffect(() => {
    if (slug && slug !== activeSlug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug) || categories[0],
    [activeSlug],
  );

  const filteredComics = useMemo(() => {
    let result: Comic[];
    if (activeSlug === 'all') {
      result = [...comics];
    } else {
      result = comics.filter((c) => c.category === activeSlug);
    }

    if (sortBy === 'popularity') {
      result.sort((a, b) => b.popularity - a.popularity);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'latest') {
      result.sort((a, b) => {
        const aNum = parseInt(a.latestChapter.match(/\d+/)?.[0] || '0', 10);
        const bNum = parseInt(b.latestChapter.match(/\d+/)?.[0] || '0', 10);
        return bNum - aNum;
      });
    }
    return result;
  }, [activeSlug, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const sortLabels: { key: 'popularity' | 'rating' | 'latest'; label: string }[] = [
    { key: 'popularity', label: '人气最高' },
    { key: 'rating', label: '评分最高' },
    { key: 'latest', label: '最新更新' },
  ];

  const pageTitle = activeSlug === 'all'
    ? '漫画分类 – 全部漫画 | JMComic 官方入口'
    : `${currentCategory.name}漫画 – ${currentCategory.description} | JMComic`;
  const pageDesc = activeSlug === 'all'
    ? '浏览 JMComic 平台全部漫画资源，涵盖热血少年、恋爱校园、奇幻冒险、搞笑日常、悬疑恐怖、科幻未来、治愈日常等多种题材。'
    : `浏览 JMComic 平台${currentCategory.name}类漫画资源。${currentCategory.description}，精选优质漫画作品，提供流畅阅读体验。`;

  const pageKeywords = activeSlug === 'all'
    ? 'jmcomic漫画,jmcomic漫画分类,漫画在线阅读,漫画推荐'
    : `jmcomic${currentCategory.name}漫画,${currentCategory.name}漫画推荐,${currentCategory.name}漫画在线`;

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://drawgates.github.io/category/${activeSlug}`,
    isPartOf: { '@type': 'WebSite', name: 'JMComic', url: 'https://drawgates.github.io' },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categoryJsonLd)}</script>
      <ScrollToTop />
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://drawgates.github.io/category/${activeSlug}`}
        ogImage="https://readdy.ai/api/search-image?query=A%20colorful%20manga%20collection%20display%20with%20various%20comic%20book%20covers%20arranged%20in%20a%20grid%2C%20bright%20and%20vibrant%20colors%2C%20library%20or%20bookstore%20style%2C%20warm%20lighting%2C%20organized%20and%20clean%20composition&width=1200&height=630&seq=jmcomic-og-category&orientation=landscape"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-6xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '漫画分类', to: '/category/all' },
                  { label: currentCategory.name },
                ]}
              />
            </div>

            {/* Page Title */}
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)] mb-2">
                {activeSlug === 'all' ? '全部漫画' : currentCategory.name + '漫画'}
              </h1>
              <p className="text-foreground-600 text-sm">{currentCategory.description}</p>
            </div>

            {/* Category Tabs */}
            <div className="mb-8 overflow-x-auto">
              <div className="flex items-center gap-2 min-w-max">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`} />
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort Options */}
            <div className="flex items-center gap-2 mb-6">
              <span className="text-xs text-foreground-500 mr-1">排序：</span>
              {sortLabels.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSortBy(s.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                    sortBy === s.key
                      ? 'bg-secondary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Results Count */}
            <p className="text-xs text-foreground-500 mb-4">
              共找到 <strong className="text-foreground-700">{filteredComics.length}</strong> 部漫画
            </p>

            {/* Comics Grid */}
            {filteredComics.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-unhappy-line text-2xl text-foreground-400" />
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无漫画</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/category/${comic.category}`}
                    className="group"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-2 aspect-[2/3]">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            comic.updateStatus === '连载中'
                              ? 'bg-primary-500 text-white'
                              : 'bg-foreground-700 text-white'
                          }`}
                        >
                          {comic.updateStatus}
                        </span>
                      </div>
                      <div className="absolute top-2 right-2 bg-white/90 text-foreground-950 text-xs px-1.5 py-0.5 rounded-full font-semibold">
                        {comic.rating}
                      </div>
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 pt-8">
                        <p className="text-white text-xs font-medium truncate">{comic.latestChapter}</p>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors truncate">
                      {comic.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{comic.author}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}